import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, Check, X, Zap } from 'lucide-react';
import { SupportAPI } from '../../api';
import { toast } from 'sonner';

const PLACEHOLDERS = ['{{first_name}}', '{{name}}', '{{email}}', '{{phone}}'];

const Editor = ({ initial, onSave, onCancel, busy }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  return (
    <div className="space-y-2">
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short name your team will recognise"
        data-testid="canned-title-input"
        className="w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551]" />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="The message customer service will send"
        data-testid="canned-body-input"
        className="w-full px-3 py-2 border border-[#D5D7ED] rounded-md text-[14px] focus:outline-none focus:border-[#0A1551]" />
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[12px] text-[#6F76A7]">Insert:</span>
        {PLACEHOLDERS.map((p) => (
          <button key={p} type="button" onClick={() => setBody((b) => `${b}${p}`)} data-testid={`canned-placeholder-${p.replace(/[{}]/g, '')}`}
            className="px-2 py-1 rounded-full bg-[#EEF2FF] text-[#4A517D] text-[11px] font-semibold hover:bg-[#E0E6FF] transition-colors">
            {p}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button onClick={() => onSave(title, body)} disabled={!title.trim() || !body.trim() || busy} data-testid="canned-save"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#0F766E] text-white text-[13px] font-semibold disabled:opacity-50">
          <Check className="w-3.5 h-3.5" /> Save reply
        </button>
        <button onClick={onCancel} data-testid="canned-cancel" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D5D7ED] text-[13px] font-medium text-[#0A1551]">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
};

const CannedRepliesPanel = () => {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => SupportAPI.cannedReplies().then(setItems)
    .catch((e) => { setItems([]); toast.error(e?.response?.data?.detail || 'Failed to load saved replies'); });
  useEffect(() => { load(); }, []);

  const wrap = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      setAdding(false);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[760px]" data-testid="admin-canned">
      <p className="text-[13px] text-[#6F76A7] mb-4">
        Answers your team can drop into a chat in one click, from the lightning button next to the reply box.
        Use <span className="font-semibold text-[#4A517D]">{'{{first_name}}'}</span> or <span className="font-semibold text-[#4A517D]">{'{{email}}'}</span> and
        they are replaced with that customer&apos;s real details as it is inserted. The replies used most often appear first.
      </p>

      {items === null ? <p className="text-[13px] text-[#6F76A7]">Loading…</p> : items.length === 0 && !adding ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-10 text-center" data-testid="canned-panel-empty">
          <Zap className="w-8 h-8 mx-auto text-[#B0B4D0] mb-2" />
          <p className="text-[14px] text-[#6F76A7]">No saved replies yet — add your first one below.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-[#E3E5F5] rounded-xl p-4" data-testid={`canned-row-${r.id}`}>
              {editId === r.id ? (
                <Editor initial={r} busy={busy} onCancel={() => setEditId(null)}
                  onSave={(t, b) => wrap(() => SupportAPI.updateCanned(r.id, t, b), 'Saved reply updated')} />
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <p className="text-[14px] font-semibold text-[#0A1551] flex-1">{r.title}</p>
                    {r.uses > 0 && (
                      <span className="px-2 py-0.5 rounded-full bg-[#E6F4F2] text-[#0F766E] text-[11px] font-bold" data-testid={`canned-uses-${r.id}`}>
                        used {r.uses}×
                      </span>
                    )}
                    <button onClick={() => setEditId(r.id)} data-testid={`canned-edit-${r.id}`} className="p-1.5 rounded hover:bg-[#F0F1FB] text-[#6F76A7]" aria-label="Edit saved reply"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => wrap(() => SupportAPI.deleteCanned(r.id), 'Saved reply removed')} data-testid={`canned-delete-${r.id}`} className="p-1.5 rounded hover:bg-[#FEF2F2] text-[#E5387B]" aria-label="Delete saved reply"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-[13px] text-[#4A517D] mt-1 whitespace-pre-line">{r.body}</p>
                  {r.updated_by && r.updated_by !== 'system' && <p className="text-[11px] text-[#9599C0] mt-2">Last edited by {r.updated_by}</p>}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="bg-white border border-[#E3E5F5] rounded-xl p-4 mt-3">
          <Editor busy={busy} onCancel={() => setAdding(false)} onSave={(t, b) => wrap(() => SupportAPI.createCanned(t, b), 'Saved reply added')} />
        </div>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="canned-add"
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2.5 rounded-md bg-[#0A1551] text-white text-[13px] font-semibold hover:bg-[#141F6B] transition-colors">
          <Plus className="w-4 h-4" /> Add a saved reply
        </button>
      )}
    </div>
  );
};

export default CannedRepliesPanel;
